<template>
<v-dialog
  v-model="localChecklistDialog"
  fullscreen
  hide-overlay
  persistent               
  transition="dialog-bottom-transition"
>
    <v-card>
      <!-- App Bar -->
      <v-app-bar dark color="blue darken-3">
        <v-toolbar-title>Add Checklist</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="promptCloseDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>

      <!-- Loader -->
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="blue"
        class="mt-5"
      ></v-progress-circular>

      <!-- Success Snackbar -->
      <v-snackbar v-model="showSuccessPrompt" color="green" top>
        Checklist saved successfully!
        <v-btn icon @click="showSuccessPrompt = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-snackbar>

      <!-- Validation Error Snackbar -->
      <v-snackbar v-model="showValidationError" color="red" top>
        Please answer all checklist items before saving.
        <v-btn icon @click="showValidationError = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-snackbar>

      <!-- Validation Error Dialog -->
      <v-dialog v-model="validationErrorDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Incomplete Checklist</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            Some checklist items are not answered. Please select "Yes" or "No" for all items before saving.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green" text @click="validationErrorDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Checklist Content -->
      <v-card-text v-if="!loading && checklist.length">
        <v-container>
          <v-row dense>
            <v-col cols="12">
              <v-card
                v-for="(section, sectionIndex) in checklist"
                :key="sectionIndex"
                class="mb-4 elevation-2"
              >
                <v-card-title class="section-title">
                  {{ getChecklistHeader(section.sectionTitle) }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" class="checklist-header">
                      <h4>{{ getChecklistHeader(section.sectionTitle) }}</h4>
                    </v-col>

                    <v-col
                      cols="12"
                      v-for="(item, index) in section.items"
                      :key="index"
                      class="my-2"
                    >
                      <v-row dense align="center">
                        <v-col cols="6">
                          <span>{{ item.label }}</span>
                        </v-col>
                        <v-col cols="6">
                          <!-- Radio Group for Yes/No -->
                        <v-radio-group
  v-model="item.checked"
  :rules="[v => (v === 'Yes' || v === 'No') || 'Please select Yes or No']"
  row
  dense
  :error="item.checked === null"                
  :class="{ 'missing-radio': item.checked === null }"  
>
                            <v-radio label="Yes" value="Yes" @click="handleCheckboxChange(item, 'Yes')"></v-radio>
                            <v-radio label="No" value="No" @click="handleCheckboxChange(item, 'No')"></v-radio>
                          </v-radio-group>
                        </v-col>
                      </v-row>

                      <!-- Conditional Text Fields -->
                      <v-row dense v-if="item.checked === 'Yes'">
                        <v-col cols="12">
                          <v-text-field
                            v-if="item.showseal1"
                            v-model="item.seal1"
                            label="Any visible damages"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showJobNumberField"
                            v-model="item.jobNumberField"
                            label="Job Number"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showseal2"
                            v-model="item.seal2"
                            label="Special fitting on seal"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showseal3"
                            v-model="item.seal3"
                            label="Note Previous Job Number"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showseal4"
                            v-model="item.seal4"
                            label="Add photo of anything unusual"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>

                          <!-- Additional Conditional Fields -->
                          <v-text-field
                            v-if="item.showsettingclipfield"
                            v-model="item.settingclipfield"
                            label="Setting Clip"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showoringMaterial"
                            v-model="item.oringMaterial"
                            label="O-Ring Material"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showoringdamage"
                            v-model="item.oringdamage"
                            label="O-Ring Damages"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showoringSize"
                            v-model="item.oringSize"
                            label="Size"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace1"
                            v-model="item.Face1"
                            label="Replace inboard stationary face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace2"
                            v-model="item.Face2"
                            label="Replace inboard Rotary face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace3"
                            v-model="item.Face3"
                            label="Result of vacuum test"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showFace4"
                            v-model="item.Face4"
                            label="Other Damage to faces"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showOutboardFace1"
                            v-model="item.OutboardFace1"
                            label="Replace outboard stationary face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showOutboardFace2"
                            v-model="item.OutboardFace2"
                            label="Replace outboard Rotary Face"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland1"
                            v-model="item.Gland1"
                            label="O-ring surface NOT acceptable"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland2"
                            v-model="item.Gland2"
                            label="Damage around setting clip area"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland3"
                            v-model="item.Gland3"
                            label="Damage on gasket/o-ring surface"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland4"
                            v-model="item.Gland4"
                            label="Pitting seen on gland"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland5"
                            v-model="item.Gland5"
                            label="Gland Port Size"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland6"
                            v-model="item.Gland6"
                            label="Spring holes damaged"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland7"
                            v-model="item.Gland7"
                            label="Dents/external damage"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland8"
                            v-model="item.Gland8"
                            label="Pins Bent loose, worn, or missing"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showGland9"
                            v-model="item.Gland9"
                            label="Other"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd1"
                            v-model="item.add1"
                            label="Lip Seals"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd2"
                            v-model="item.add2"
                            label="Tool Required 1"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd3"
                            v-model="item.add3"
                            label="Tool Required 2"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd4"
                            v-model="item.add4"
                            label="Tool Required 3"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd5"
                            v-model="item.add5"
                            label="Additional Part 1"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd6"
                            v-model="item.add6"
                            label="Additional Part 2"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showadd7"
                            v-model="item.add7"
                            label="Metal Bellows"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck1"
                            v-model="item.check1"
                            label="Ovality on sealing surface"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck2"
                            v-model="item.check2"
                            label="Damage on Other Parts"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck3"
                            v-model="item.check3"
                            label="Additional parts"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck4"
                            v-model="item.check4"
                            label="Machining to be Done"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                          <v-text-field
                            v-if="item.showcheck6"
                            v-model="item.check6"
                            label="Other parts and components"
                            rounded
                            dense
                            filled
                            class="ml-3"
                            :rules="[v => !!v || 'This field is required']"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Upload and View Photo Buttons -->
                    <v-col cols="12" class="d-flex justify-start">
                      <!-- Upload Photo Button -->
                      <v-btn
                        rounded
                        dark
                        color="blue"
                        @click="openUploadOptionsDialog(sectionIndex)"
                        class="my-2"
                      >
                        <v-icon left>mdi-camera</v-icon>Upload Photo
                      </v-btn>

                      <!-- View Photos Button -->
                      <v-btn
                        v-if="section.photos.length"
                        rounded
                        dark
                        color="green"
                        @click="openPhotoDialog(section.photos, section.sectionTitle)"
                        class="my-2 ml-2"
                      >
                        <v-icon left>mdi-eye</v-icon>View Photos
                      </v-btn>
                    </v-col>

                    <!-- Upload Loader -->
                    <v-col cols="12" v-if="uploadingPhoto">
                      <v-progress-linear
                        indeterminate
                        color="blue"
                        class="mb-2"
                      ></v-progress-linear>
                      <span>Uploading photos...</span>
                    </v-col>

                    <!-- Upload Photos Input -->
                    <input
                      :ref="'fileInput' + sectionIndex"
                      type="file"
                      accept="image/*"
                      multiple
                      @change="handleFileUpload($event, sectionIndex)"
                      style="display: none"
                    />
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- Save and Close Buttons -->
      <v-card-actions v-if="!loading && checklist.length" class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          dark
          rounded
          dense
          width="150px"
          @click="attemptSaveChecklist"
          color="blue"
        >
          Save Checklist
        </v-btn>
        <v-btn
          color="red"
          dark
          rounded
          dense
          width="150px"
          @click="promptCloseDialog"
        >
          Close
        </v-btn>
      </v-card-actions>

      <!-- Close Confirmation Dialog -->
      <v-dialog v-model="closeDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Confirm Close</v-card-title>
          <v-divider></v-divider>
          <v-card-text>Are you sure you want to close the checklist?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green" text @click="saveAndExit">Save and Exit</v-btn>
            <v-btn color="blue" text @click="closeCloseDialog">Return to Checklist</v-btn>
            <v-btn color="red" text @click="confirmCloseWithoutSaving">Exit without Saving</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Photo Dialog -->
      <v-dialog v-model="photoDialog" max-width="600px">
        <v-card>
          <v-toolbar dark color="primary">
            <v-toolbar-title>{{ currentPhotoLabel }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="closePhotoDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-carousel v-model="carouselIndex">
            <v-carousel-item
              v-for="(photo, index) in selectedPhotos"
              :key="index"
              :src="photo"
            ></v-carousel-item>
          </v-carousel>
        </v-card>
      </v-dialog>

      <!-- Upload Options Dialog -->
      <v-dialog v-model="uploadOptionsDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">
            Select Photo Upload Method
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row class="pa-4" justify="center" align="center">
              <v-col cols="12">
                <v-btn
                  class="upload-btn my-2"
                  @click="takePhoto"
                  block
                  elevation="2"
                >
                  <v-icon left class="upload-icon">mdi-camera</v-icon>
                  Use Camera
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  class="upload-btn my-2"
                  @click="uploadFromGallery"
                  block
                  elevation="2"
                >
                  <v-icon left class="upload-icon">mdi-image</v-icon>
                  Upload from Gallery
                </v-btn>
              </v-col>
              <v-col cols="12">
                <v-btn
                  class="upload-btn my-2"
                  @click="uploadFromFiles"
                  block
                  elevation="2"
                >
                  <v-icon left class="upload-icon">mdi-folder</v-icon>
                  Upload from Files
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="black" @click="closeUploadOptionsDialog">
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { firebaseApp } from "@/firebaseConfig";

export default {
  props: {
    jobNumber: {
      type: String,
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localChecklistDialog: this.value,
      checklist: [],
      loading: true,
      uploadingPhoto: false,
      checklistSaved: false,
      showSuccessPrompt: false,
      showValidationError: false,
      validationErrorDialog: false,
      photoDialog: false,
      uploadOptionsDialog: false,
      selectedPhotos: [],
      carouselIndex: 0,
      currentPhotoLabel: "",
      closeDialog: false,
      photoLabels: [],
      selectedSectionIndex: null,
      defaultChecklist: [
        {
          sectionTitle: "NOTE",
          items: [
            {
              label: "Seal Failure Report Requested?",
              checked: null,
              upload: false,
            },
            {
              label: "Any visible damage?",
              checked: null,
              upload: true,
              showseal1: true,
              seal1: "",
            },
            {
              label: "Special Fittings on seal?",
              checked: null,
              upload: true,
              showseal2: true,
              seal2: "",
            },
            {
              label: "Note Previous Job Number",
              checked: null,
              showJobNumberField: true,
              jobNumberField: "",
            },
            {
              label: "Add Photo of anything unusual",
              checked: null,
              upload: true,
              showseal4: true,
              seal4: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "SETTING CLIPS",
          items: [
            {
              label: "Setting Clips present",
              checked: null,
              upload: true,
            },
            {
              label: "Setting Clips reusable",
              checked: null,
              showsettingclipfield: true,
              settingclipfield: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "O-RINGS",
          items: [
            {
              label: "O-ring Material",
              checked: null,
              showoringMaterial: true,
              oringMaterial: "",
            },
            {
              label: "O-ring damages",
              checked: null,
              upload: true,
              showoringdamage: true,
              oringdamage: "",
            },
            {
              label: "Special O-ring Sizes",
              checked: null,
              upload: true,
              showoringSize: true,
              oringSize: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "INBOARD FACES",
          items: [
            {
              label: "Replace inboard stationary face",
              checked: null,
              upload: true,
              showFace1: true,
              Face1: "",
            },
            {
              label: "Replace inboard Rotary face",
              checked: null,
              upload: true,
              showFace2: true,
              Face2: "",
            },
            {
              label: "Result of Vacuum test (if required)",
              checked: null,
              showFace3: true,
              Face3: "",
            },
            {
              label: "Other Damage on faces",
              checked: null,
              upload: true,
              showFace4: true,
              Face4: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "OUTBOARD FACES (if applicable)",
          items: [
            {
              label: "Replace outboard stationary face",
              checked: null,
              upload: true,
              showOutboardFace1: true,
              OutboardFace1: "",
            },
            {
              label: "Replace outboard Rotary face",
              checked: null,
              upload: true,
              showOutboardFace2: true,
              OutboardFace2: "",
            },
            {
              label: "Result of Vacuum test (if required)",
              checked: null,
              showOutboardFace3: true,
              OutboardFace3: "",
            },
            {
              label: "Other Damage on faces",
              checked: null,
              upload: true,
              showOutboardFace4: true,
              OutboardFace4: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "GLAND",
          items: [
            {
              label: "O-ring surfaces NOT acceptable",
              checked: null,
              upload: true,
              showGland1: true,
              Gland1: "",
            },
            {
              label: "Damage around setting clip area",
              checked: null,
              upload: true,
              showGland2: true,
              Gland2: "",
            },
            {
              label: "Damage on gasket/o-ring surface",
              checked: null,
              upload: true,
              showGland3: true,
              Gland3: "",
            },
            {
              label: "Pitting seen on gland",
              checked: null,
              upload: true,
              showGland4: true,
              Gland4: "",
            },
            {
              label: "Gland port sizes",
              checked: null,
              upload: true,
              showGland5: true,
              Gland5: "",
            },
            {
              label: "Spring holes damaged",
              checked: null,
              upload: true,
              showGland6: true,
              Gland6: "",
            },
            {
              label: "Dents/external damage",
              checked: null,
              upload: true,
              showGland7: true,
              Gland7: "",
            },
            {
              label: "Pins: Bent, Loose, worn, missing",
              checked: null,
              upload: true,
              showGland8: true,
              Gland8: "",
            },
            {
              label: "Other",
              checked: null,
              upload: true,
              showGland9: true,
              Gland9: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "ADDITIONAL ITEMS",
          items: [
            {
              label: "Lip seals",
              checked: null,
              upload: true,
              showadd1: true,
              add1: "",
            },
            {
              label: "Tool required 1",
              checked: null,
              upload: true,
              showadd2: true,
              add2: "",
            },
            {
              label: "Tool required 2",
              checked: null,
              upload: true,
              showadd3: true,
              add3: "",
            },
            {
              label: "Tool required 3",
              checked: null,
              upload: true,
              showadd4: true,
              add4: "",
            },
            {
              label: "Additional part 1",
              checked: null,
              upload: true,
              showadd5: true,
              add5: "",
            },
            {
              label: "Additional part 2",
              checked: null,
              upload: true,
              showadd6: true,
              add6: "",
            },
            {
              label: "Metal bellows",
              checked: null,
              upload: true,
              showadd7: true,
              add7: "",
            },
            {
              label: "Other",
              checked: null,
              upload: true,
              showadd8: true,
              add8: "",
            },
          ],
          photos: [],
        },
        {
          sectionTitle: "CHECKS",
          items: [
            {
              label: "Ovality on sealing surfaces",
              checked: null,
              upload: true,
              showcheck1: true,
              check1: "",
            },
            {
              label: "Damage on other parts",
              checked: null,
              upload: true,
              showcheck2: true,
              check2: "",
            },
            {
              label: "Additional parts (Shaft sleeve, Backplate, etc.)",
              checked: null,
              upload: true,
              showcheck3: true,
              check3: "",
            },
            {
              label: "Note of machining to be done",
              checked: null,
              upload: true,
              showcheck4: true,
              check4: "",
            },
            {
              label: "Punch gland with new Job Number (if applicable)",
              checked: null,
              upload: true,
              showcheck5: true,
              check5: "",
            },
            {
              label: "Other parts or components",
              checked: null,
              upload: true,
              showcheck6: true,
              check6: "",
            },
          ],
          photos: [],
        },
      ],
    };
  },
  watch: {
    value(val) {
      this.localChecklistDialog = val;
      if (val && this.jobNumber) {
        this.fetchChecklistData(this.jobNumber);
      }
    },
    localChecklistDialog(val) {
      this.$emit("input", val);
    },
    carouselIndex(newIndex) {
      this.currentPhotoLabel = this.photoLabels[newIndex] || "";
    },
  },
  computed: {
    // Check if every item in every section is answered "Yes" or "No"
    isFormValid() {
      return this.checklist.every((section) =>
        section.items.every((item) => item.checked === "Yes" || item.checked === "No")
      );
    },
  },
  methods: {
    isItemAnswered(item) {
      return item.checked === "Yes" || item.checked === "No";
    },
    handleCheckboxChange(item, value) {
      if (value === "Yes") {
        if (item.checked !== "Yes") {
          this.$set(item, "checked", "Yes");
        }
      } else if (value === "No") {
        if (item.checked !== "No") {
          this.$set(item, "checked", "No");
        }
      }

      // If 'No' is selected, clear and hide dependent fields
      if (item.checked === "No") {
        this.clearDependentFields(item);
      }

      // Toggle visibility of fields based on the item label and checked state
      this.toggleFieldVisibility(item);
    },
    clearDependentFields(item) {
      for (const key in item) {
        if (key.startsWith("show")) {
          this.$set(item, key, false);
        }
        if (key.match(/^(seal|Gland|add|check|Face|OutboardFace|oring|settingclipfield|jobNumberField)$/)) {
          if (typeof item[key] === 'string') {
            this.$set(item, key, "");
          } else if (typeof item[key] === 'boolean') {
            this.$set(item, key, false);
          }
        }
      }
    },
    toggleFieldVisibility(item) {
      const label = item.label;

      const visibilityMap = {
        "Special Fittings on seal?": "showseal2",
        "Note Previous Job Number": "showJobNumberField",
        "Add Photo of anything unusual": "showseal4",
        "Setting Clips reusable": "showsettingclipfield",
        "O-ring Material": "showoringMaterial",
        "O-ring damages": "showoringdamage",
        "Special O-ring Sizes": "showoringSize",
        "Replace inboard stationary face": "showFace1",
        "Replace inboard Rotary face": "showFace2",
        "Result of Vacuum test (if required)": ["showFace3", "showOutboardFace3"],
        "Other Damage on faces": ["showFace4", "showOutboardFace4"],
        "Replace outboard stationary face": "showOutboardFace1",
        "Replace outboard Rotary face": "showOutboardFace2",
        "O-ring surfaces NOT acceptable": "showGland1",
        "Damage around setting clip area": "showGland2",
        "Damage on gasket/o-ring surface": "showGland3",
        "Pitting seen on gland": "showGland4",
        "Gland port sizes": "showGland5",
        "Spring holes damaged": "showGland6",
        "Dents/external damage": "showGland7",
        "Pins: Bent, Loose, worn, missing": "showGland8",
        "Other": "showGland9",
        "Lip seals": "showadd1",
        "Tool required 1": "showadd2",
        "Tool required 2": "showadd3",
        "Tool required 3": "showadd4",
        "Additional part 1": "showadd5",
        "Additional part 2": "showadd6",
        "Metal bellows": "showadd7",
        "Ovality on sealing surfaces": "showcheck1",
        "Damage on other parts": "showcheck2",
        "Additional parts (Shaft sleeve, Backplate, etc.)": "showcheck3",
        "Note of machining to be done": "showcheck4",
        "Punch gland with new Job Number (if applicable)": "showcheck5",
        "Other parts or components": "showcheck6",
      };

      if (visibilityMap[label]) {
        const fields = Array.isArray(visibilityMap[label])
          ? visibilityMap[label]
          : [visibilityMap[label]];
        fields.forEach((field) => {
          this.$set(item, field, item.checked === "Yes");
        });
      }
    },
    openPhotoDialog(photos, label) {
      this.selectedPhotos = photos;
      this.photoLabels = new Array(photos.length).fill(label);
      this.currentPhotoLabel = label;
      this.carouselIndex = 0;
      this.photoDialog = true;
    },
    closePhotoDialog() {
      this.photoDialog = false;
    },
    async fetchChecklistData(jobNumber) {
      this.loading = true;
      try {
        const db = getFirestore(firebaseApp);
        const checklistRef = collection(db, "checklists");
        const q = query(checklistRef, where("jobNumber", "==", jobNumber));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          const checklistData = JSON.parse(JSON.stringify(docData.checklist));
          this.checklist = checklistData.map((section) => ({
            sectionTitle: section.sectionTitle,
            items: section.items.map((item) => ({
              ...item,
              checked: (item.checked === "Yes" || item.checked === "No") ? item.checked : null,
            })),
            photos: section.photos || [],
          }));
        } else {
          this.checklist = JSON.parse(JSON.stringify(this.defaultChecklist));
        }
      } catch (error) {
        console.error("Error fetching checklist data:", error);
      } finally {
        this.loading = false;
      }
    },
  promptCloseDialog() {
  if (!this.isFormValid) {          // 🔹 block if anything missing
    this.validationErrorDialog = true;
    this.showValidationError = true;
    return;
  }
  this.closeDialog = true;
},

    // Attempt to save the checklist after validating
    async attemptSaveChecklist() {
      if (!this.isFormValid) {
        this.validationErrorDialog = true;
        this.showValidationError = true;
        return;
      }
      await this.saveChecklist();
    },
    async saveChecklist() {
      const db = getFirestore(firebaseApp);

      // Prepare the checklist data for saving
      const checklistData = this.checklist.map((section) => ({
        sectionTitle: section.sectionTitle,
        items: section.items
          .filter((item) => item.checked === "Yes" || item.checked === "No")
          .map((item) => {
            const savedItem = {
              label: item.label,
              checked: item.checked,
            };

            // Conditionally include fields
            const fieldsToCheck = [
              "seal1", "seal2", "seal3", "seal4",
              "settingclipfield", "oringMaterial", "oringdamage", "oringSize",
              "Face1", "Face2", "Face3", "Face4",
              "OutboardFace1", "OutboardFace2", "OutboardFace3", "OutboardFace4",
              "Gland1", "Gland2", "Gland3", "Gland4", "Gland5", "Gland6", "Gland7", "Gland8", "Gland9",
              "add1", "add2", "add3", "add4", "add5", "add6", "add7", "add8",
              "check1", "check2", "check3", "check4", "check5", "check6",
              "jobNumberField"
            ];

            fieldsToCheck.forEach((field) => {
              let showProp = "";
              if (field.startsWith("seal")) showProp = "showseal" + field.slice(4);
              else if (field.startsWith("Gland")) showProp = "showGland" + field.slice(5);
              else if (field.startsWith("add")) showProp = "showadd" + field.slice(3);
              else if (field.startsWith("check")) showProp = "showcheck" + field.slice(5);
              else if (field === "oringMaterial") showProp = "showoringMaterial";
              else if (field === "oringdamage") showProp = "showoringdamage";
              else if (field === "oringSize") showProp = "showoringSize";
              else if (field === "settingclipfield") showProp = "showsettingclipfield";
              else if (field.startsWith("Face")) showProp = "showFace" + field.slice(4);
              else if (field.startsWith("OutboardFace")) showProp = "showOutboardFace" + field.slice(12);
              else if (field === "jobNumberField") showProp = "showJobNumberField";

              if (item[showProp] && item[field]) {
                savedItem[field] = item[field];
              }
            });

            return savedItem;
          }),
        photos: section.photos || [],
      }));

      const checklistDoc = {
        jobNumber: this.jobNumber,
        checklist: checklistData,
      };

      try {
        const dbFirestore = getFirestore(firebaseApp);
        const checklistRef = collection(dbFirestore, "checklists");
        const q = query(checklistRef, where("jobNumber", "==", this.jobNumber));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Update existing checklist
          const docId = querySnapshot.docs[0].id;
          const docRef = doc(dbFirestore, "checklists", docId);
          await updateDoc(docRef, { checklist: checklistData });
        } else {
          // Add new checklist
          await addDoc(collection(db, "checklists"), checklistDoc);
        }

        this.showSuccessPrompt = true;
        this.checklistSaved = true;
        this.localChecklistDialog = false;
      } catch (error) {
        console.error("Error saving checklist:", error);
        this.showValidationError = true;
      }
    },
    async saveAndExit() {
      if (!this.isFormValid) {
        this.validationErrorDialog = true;
        this.showValidationError = true;
        return;
      }
      await this.saveChecklist();
      this.localChecklistDialog = false;
      this.$emit("input", false);
      this.closeDialog = false;
    },
   confirmCloseWithoutSaving() {
  if (!this.isFormValid) {          // 🔹 same guard here
    this.validationErrorDialog = true;
    this.showValidationError = true;
    return;
  }
  this.localChecklistDialog = false;
  this.$emit('input', false);
  this.closeDialog = false;
},
    openUploadOptionsDialog(sectionIndex) {
      this.selectedSectionIndex = sectionIndex;
      this.uploadOptionsDialog = true;
    },
    closeUploadOptionsDialog() {
      this.uploadOptionsDialog = false;
    },
    async takePhoto() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.setAttribute("capture", "camera");
      fileInput.click();
    },
    uploadFromGallery() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute("capture");
      fileInput.setAttribute("accept", "image/*");
      fileInput.click();
    },
    uploadFromFiles() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute("capture");
      fileInput.setAttribute("accept", "*/*");
      fileInput.click();
    },
    async handleFileUpload(event, sectionIndex) {
      const files = event.target.files;
      const storage = getStorage(firebaseApp);
      const photos = [];

      this.uploadingPhoto = true;
      try {
        for (const file of files) {
          const storageReference = storageRef(
            storage,
            `checklist/${this.jobNumber}/${file.name}`
          );
          const snapshot = await uploadBytes(storageReference, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          photos.push(downloadURL);
        }
        this.checklist[sectionIndex].photos.push(...photos);
      } catch (error) {
        console.error("Error uploading files:", error);
        this.showValidationError = true;
      } finally {
        this.uploadingPhoto = false;
      }
    },
    getChecklistHeader(sectionTitle) {
      const headers = {
        NOTE: "NOTE",
        "SETTING CLIPS": "SETTING CLIPS",
        "O-RINGS": "O-RINGS",
        "INBOARD FACES": "INBOARD FACES",
        "OUTBOARD FACES (if applicable)": "OUTBOARD FACES",
        GLAND: "GLAND",
        "ADDITIONAL ITEMS": "ADDITIONAL ITEMS",
        CHECKS: "CHECKS",
      };
      return headers[sectionTitle] || "Checklist Details";
    },
    closeCloseDialog() {
      this.closeDialog = false;
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 24px;
  font-weight: bold;
  color: #1976d2;
  margin-top: 16px;
  margin-bottom: 10px;
}

.checklist-header {
  margin-top: 20px;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 18px;
  color: #424242;
}

.v-card {
  border-radius: 16px;
  background-color: #fafafa;
}

.v-btn {
  font-size: 14px;
  font-weight: bold;
}

.v-toolbar-title {
  font-weight: bold;
  font-size: 22px;
}

.v-progress-circular {
  margin-top: 50px;
}

.v-progress-linear {
  margin-top: 10px;
}

.upload-btn {
  background-color: #ffffff;
  color: #000000;
  font-size: 16px;
  padding: 12px;
  border: 1px solid #cccccc;
}

.upload-btn:hover {
  background-color: #f5f5f5;
}

.upload-icon {
  color: #000000;
  margin-right: 10px;
}
.missing-radio {
  border: 1px solid red !important;
  border-radius: 4px;
  padding: 4px;
}
</style>
